<template>
    <b-modal
        ref="modalSavePatientNote"
        title-tag="h2"
        :title="isEdit ? 'Edit Note' : 'New Note'"
        no-close-on-backdrop
        lazy
    >
        <form ref="form" name="new-form">
            <b-form-group label-for="title" label="Title:" label-cols="4">
                <b-form-input
                    id="title"
                    v-model="title"
                    :class="{ 'border-danger': errors.has('title') }"
                    :disabled="!editPermission.title"
                    v-validate="'required'"
                    placeholder="Title (required)"
                    name="title"
                ></b-form-input>
            </b-form-group>
            <b-form-group
                label-for="notification"
                label="Priority:"
                label-cols="4"
            >
                <b-form-select
                    v-model="priority"
                    :options="priorities"
                    :disabled="!editPermission.priority"
                    value-field="value"
                    text-field="text"
                    name="priority"
                ></b-form-select>
            </b-form-group>
            <b-form-group
                label-for="notification"
                label="Notification:"
                label-cols="4"
            >
                <b-form-select
                    v-model="notification"
                    :options="notifications"
                    :disabled="!editPermission.notification"
                    value-field="value"
                    text-field="text"
                    name="notification"
                ></b-form-select>
            </b-form-group>
            <b-form-group label-for="note" label="Note:" label-cols="4">
                <editable
                    id="note"
                    v-model="note"
                    placeholder="Note"
                    :disabled="!editPermission.note"
                    name="note"
                />
            </b-form-group>
        </form>
        <template v-slot:modal-footer="{ cancel, close }">
            <div class="w-100">
                <b-button
                    v-if="!isLoading && !isViewOnly"
                    @click="cancel"
                    class="float-left"
                    variant="secondary"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <loader v-if="isLoading" class="m-0" />
                    <b-button-group v-if="!isLoading && !isEdit && !isViewOnly">
                        <b-dropdown
                            v-if="!continues"
                            @click="addNote(close, false)"
                            left
                            split
                            text="Save + Done"
                            variant="primary"
                            lazy
                        >
                            <b-dropdown-item @click="addNote(close, true)"
                                >Save + Add New</b-dropdown-item
                            >
                        </b-dropdown>
                        <b-dropdown
                            v-else
                            @click="addNote(close, true)"
                            left
                            split
                            text="Save + Add New"
                            variant="primary"
                        >
                            <b-dropdown-item @click="addNote(close, false)"
                                >Save + Done</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>

                    <b-button
                        v-if="!isLoading && isEdit && !isViewOnly"
                        @click="editNote(close)"
                        variant="primary"
                        >Save</b-button
                    >

                    <b-button
                        v-if="!isLoading && isViewOnly"
                        @click="cancel"
                        variant="secondary"
                        >Close</b-button
                    >
                </div>
            </div>
        </template>
    </b-modal>
</template>
<script type="text/javascript">
import { isEmpty } from "lodash";
import Notes from "@/services/patient-notes";

export default {
    components: {
        Loader: () => import("@/components/elements/loader"),
        Editable: () => import("@/components/elements/editable-textarea")
    },
    props: {
        patientId: {
            type: String
        },
        editPermission: {
            type: Object,
            default: () => {
                return {
                    title: true,
                    priority: true,
                    notification: true,
                    note: true
                };
            }
        }
    },
    computed: {
        isViewOnly() {
            return Object.values(this.editPermission).every(() => false);
        }
    },
    data() {
        return {
            note: "",
            title: "",
            notification: 0,
            notifications: [
                { value: 0, text: "Profile Only" },
                { value: 1, text: "Pop-up" }
            ],
            priority: 0,
            priorities: [
                { value: 0, text: "Low" },
                { value: 1, text: "Medium" },
                { value: 2, text: "High" }
            ],
            continues: false,
            isLoading: false,
            isEdit: false,
            noteId: null
        };
    },
    methods: {
        open(note) {
            this.$nextTick(() => {
                if (!isEmpty(note)) {
                    let editNote = _.clone(note);

                    this.isEdit = true;
                    this.title = editNote.content;
                    this.priority = editNote.priority;
                    this.notification = editNote.notification;
                    this.note = editNote.note;
                    this.noteId = editNote.id;
                }
            });
            this.$refs.modalSavePatientNote.show();
        },
        async addNote(closeFn, continues) {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.isLoading = true;
            this.continues = continues ? continues : false;

            let params = {
                patient_id: this.patientId,
                content: this.title,
                note: this.note,
                notification: this.notification ? this.notification : 0,
                priority: this.priority ? this.priority : 0
            };

            Notes.addNote(params)
                .then(response => {
                    if (response.message.code == 200) {
                        this._message("Note saved");
                        this.$emit("reload");
                        this.$store.dispatch(
                            "patient/loadPopupNotes",
                            this.patientId
                        );
                        if (continues) {
                            this.note = null;
                            this.title = null;
                            this.priority = 0;
                            this.notification = 0;
                            this.$validator.reset();
                            this.isLoading = false;
                        } else {
                            closeFn();
                        }
                    } else {
                        this._message("Error : Unable to save note", "danger");
                        console.error(response.message.errors);
                        this.isLoading = false;
                    }
                })
                .catch(error => {
                    this.isLoading = false;
                    this.showError(closeFn);
                    console.error(error);
                });
        },
        async editNote(closeFn) {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.isLoading = true;

            let params = {
                patient_id: this.patientId,
                content: this.title,
                note: this.note,
                notification: this.notification ? this.notification : 0,
                priority: this.priority ? this.priority : 0
            };

            Notes.editNote(this.noteId, params)
                .then(response => {
                    if (response.message.code == 200) {
                        this._message(response.resource);
                        this.$emit("reload");
                        this.$store.dispatch(
                            "patient/loadPopupNotes",
                            this.patientId
                        );
                        closeFn();
                    } else {
                        this._message("Error : Unable to save note", "danger");
                        console.error(response.message.errors);
                    }

                    this.isLoading = false;
                })
                .catch(error => {
                    this.isLoading = false;
                    this.showError();
                    console.error(error);
                });
        },
        showError(closeFn) {
            this._message("Error : Unable to save note", "danger");
            closeFn();
        }
    }
};
</script>
